import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {MatDialog} from "@angular/material/dialog";
import {Store} from "@ngrx/store";
import {FormBuilder} from "@angular/forms";
import {
    selectBaselineAsset,
    selectGeneralInformationEdit,
    selectGeneratedGeneralInfo,
    selectGptGeneralInfoIsEditable,
    selectIsAnyAssetFormInEditMode,
    selectSelectedAsset
} from "../../store/asset/asset.selectors";
import {map, Subscription, combineLatest} from "rxjs";
import {Code, CodeTableEnum} from "../../../shared/model/code";
import {selectCodesFromTable} from "../../../shared/store/shared.selectors";
import {AssetGeneralInformationActions} from "../../store/asset.actions";
import {EMPTY_CODE, TraceableCode} from "../../../shared/model/traceable";
import {noWhitespaceValidator} from "../../../shared/utils/form-validators";
import {Asset, EMPTY_ASSET} from "../../models/asset/asset";
import {ConfirmationComponent} from "../../../shared/components/confirmation/confirmation.component";
import {AssetGeneralInfo} from "../../../shared/generated-sources/api/gpt";
import {GicsSelectorDialogComponent, GicsSelectorDialogResult} from "../../../shared/components/gics-selector-dialog/gics-selector-dialog.component";

@Component({
    selector: "valumize-asset-general-information",
    templateUrl: "./asset-general-information.component.html",
    styleUrls: ["./asset-general-information.component.scss"],
    standalone: false
})
export class AssetGeneralInformationComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);

    subscriptions: Subscription[] = [];

    asset$ = this.store.select(selectSelectedAsset);
    gptGeneralInfo$ = this.store.select(selectGeneratedGeneralInfo);
    baselineAsset: Asset = EMPTY_ASSET;
    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    isEditable = false;
    isDataset = false;
    isGptEditMode = false;
    gptErrorMessage = "";
    persistedRegions: TraceableCode[] = [];
    gicDisplayValue = "-";
    private gicsCodes: Code[] = [];

    codeTableAssessment = CodeTableEnum.SHARED_ASSESSMENT;
    codeTableCountry = CodeTableEnum.SHARED_COUNTRY;
    codeTableCurrency = CodeTableEnum.SHARED_CURRENCY;
    codeTableESG = CodeTableEnum.ASSET_ESG;
    codeTableFiscalYearEnd = CodeTableEnum.SHARED_FISCALYEAREND;
    codeTableGpIndication = CodeTableEnum.ASSET_GPINDICATION;
    codeTableRegion = CodeTableEnum.SHARED_REGIONS;
    codeTableStage = CodeTableEnum.SHARED_STAGES;

    /** Advisory length for the activity descriptor; matches the clamp the scraper applies on import. */
    readonly activityDescriptionSoftLimit = 250;

    generalInformationForm = this.formBuilder.group({
        regions: this.formBuilder.control<string[]>([], {nonNullable: true}),
        country: this.formBuilder.control<string | null>(null),
        // No maxLength validator: it made the whole card unsaveable whenever the value exceeded it,
        // with no error rendered anywhere to explain why — and once US-937 taught the scraper to write
        // this field, imported descriptors did exceed it (186 chars against a 100 cap). The length is
        // advisory instead, shown as a counter by the input (activityDescriptionSoftLimit) and clamped
        // at the source by the scraper, so a value the user never typed can't block their save.
        activityDescription: this.formBuilder.control<string>(""),
        stage: this.formBuilder.control<string>("", {nonNullable: true}),
        gic: this.formBuilder.control<string>("", {nonNullable: true}),
        currency: this.formBuilder.control<string>("", {nonNullable: true, validators: noWhitespaceValidator()}),
        esg: this.formBuilder.control<string>("", {nonNullable: true}),
        assessment: this.formBuilder.control<string>("", {nonNullable: true}),
        fiscalYearEnd: this.formBuilder.control<string>("", {nonNullable: true}),
        publiclyListed: this.formBuilder.control<boolean>(false, {nonNullable: true}),
    });

    constructor() {
    }

    ngOnInit() {
        const gicsCodes$ = this.store.select(selectCodesFromTable(CodeTableEnum.SHARED_GICS));

        this.subscriptions.push(
            combineLatest([this.store.select(selectSelectedAsset), gicsCodes$]).pipe(map(([asset, gicsCodes]) => {
                    this.gicsCodes = gicsCodes;
                    this.persistedRegions = asset.data.regions;
                    this.generalInformationForm.patchValue({
                        country: asset.data.country.code,
                        activityDescription: asset.data.activityDescription.text,
                        regions: asset.data.regions.map(r => r.code ?? ""),
                        stage: asset.data.stage.code,
                        gic: asset.data.gic.code,
                        currency: asset.data.currencyIso.code,
                        esg: asset.data.esg.code,
                        fiscalYearEnd: asset.data.fiscalYearEnd.code,
                        assessment: asset.data.assessment.code,
                        publiclyListed: asset.data.publiclyListed
                    });
                    this.gicDisplayValue = this.getGicDisplayValue(asset.data.gic.code, gicsCodes);
                })
            ).subscribe());

        this.subscriptions.push(
            this.store.select(selectBaselineAsset).pipe(map((baselineAsset) => {
                if (baselineAsset.data.id) {
                    this.isDataset = true;
                    this.baselineAsset = baselineAsset.data;
                } else {
                    this.isDataset = false;
                }
            })).subscribe());

        this.subscriptions.push(
            this.store.select(selectGeneralInformationEdit).pipe(map((editable) => {
                this.isEditable = editable;
                if (editable) {
                    this.generalInformationForm.enable();
                } else {
                    this.generalInformationForm.disable();
                }
            })).subscribe());

        this.subscriptions.push(
            this.store.select(selectGptGeneralInfoIsEditable).pipe(map(isGptEditable => {
                this.isGptEditMode = isGptEditable;
            })).subscribe());

        this.subscriptions.push(
            this.gptGeneralInfo$.pipe(map(gptGeneralInfo => {
                if (gptGeneralInfo.status === "ERROR") {
                    this.gptErrorMessage = gptGeneralInfo.errorMessage;
                    this.isGptEditMode = false;
                }
            })).subscribe());
    }

    save() {
        if (this.generalInformationForm.get("country")?.value?.toString().trim().length === 0) {
            this.generalInformationForm.get("country")?.setValue(null);
        }

        const updatedRegionsArray = this.updateRegionsArray();
        this.store.dispatch(AssetGeneralInformationActions.save({
            ...this.generalInformationForm.getRawValue(),
            regions: updatedRegionsArray
        }));
    }

    editMode = () => this.store.dispatch(AssetGeneralInformationActions.edit());

    cancel = () => {
        this.ngOnInit();
        this.store.dispatch(AssetGeneralInformationActions.cancel());
    };

    gptEditMode = () => this.store.dispatch(AssetGeneralInformationActions.gptEdit());

    gptCancel = () => this.store.dispatch(AssetGeneralInformationActions.gptCancel());

    openGicsDialog(): void {
        const dialogRef = this.dialog.open(GicsSelectorDialogComponent, {
            data: {currentValue: this.generalInformationForm.controls.gic.value},
            width: "500px"
        });

        dialogRef.afterClosed().subscribe((result: GicsSelectorDialogResult | null) => {
            if (result) {
                this.generalInformationForm.controls.gic.setValue(result.selectedValue ?? "");
                this.generalInformationForm.controls.gic.markAsDirty();
                this.generalInformationForm.controls.gic.markAsTouched();
                this.gicDisplayValue = result.displayValue;
            }
        });
    }

    applyGeneratedInfo(gptGeneralInfo: AssetGeneralInfo) {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `Generated information can be factually wrong, please verify the data.`;

        dialogRef.afterClosed().pipe(map(result => {
            if (result) {
                // Apply GPT-generated values to the form
                this.generalInformationForm.patchValue({
                    regions: gptGeneralInfo.regions?.map(r => r.code ?? "") ?? [],
                    country: gptGeneralInfo.country?.code ?? null,
                    activityDescription: gptGeneralInfo.activityDescription?.text ?? "",
                    stage: gptGeneralInfo.stage?.code ?? "",
                    gic: gptGeneralInfo.gic?.code ?? "",
                    currency: gptGeneralInfo.currency?.code ?? "",
                    publiclyListed: gptGeneralInfo.publiclyListed ?? false
                });

                // Update persisted regions for save operation
                this.persistedRegions = gptGeneralInfo.regions ?? [];

                // Update GIC display value
                this.gicDisplayValue = this.getGicDisplayValue(gptGeneralInfo.gic?.code, this.gicsCodes);

                // Cancel GPT edit mode
                this.gptCancel();
            }
        })).subscribe();
    }

    formatRegions(regions: TraceableCode[] | undefined): string {
        return regions?.map(r => r.code).join(", ") ?? "";
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    private updateRegionsArray(): TraceableCode[] {
        return this.generalInformationForm.controls.regions.value
            .map(regionsFormValue => {
                const existingRegion = this.persistedRegions.find(region => region.code === regionsFormValue);
                return existingRegion || {...EMPTY_CODE, code: regionsFormValue};
            });
    }

    private getGicDisplayValue(gicCode: string | undefined, gicsCodes: Code[]): string {
        if (!gicCode) return "-";
        const found = gicsCodes.find(c => c.code === gicCode);
        if (!found) return gicCode;
        return found.translations?.["en"] || found.code || "-";
    }
}
